import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Warehouse,
  Truck,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  Clock,
  Lock,
} from "lucide-react";
import contImg from "./assets/cont.JPG";

export default function LandingPage() {
  return (
    <div className="bg-zinc-50 text-zinc-900 font-sans">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${contImg}')`,
          }}
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/90 via-zinc-900/80 to-zinc-900/90" />

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl"
          >

            <div className="inline-block mb-4 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">Secure • Reliable • Professional</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
             Sahar Self Storage Containers
            </h1>

            <p className="text-xl md:text-2xl text-zinc-200 leading-relaxed max-w-2xl mb-10">
              Professional 20ft and 40ft high cube containers in a secure, managed storage yard. 
              Perfect for personal, trade, and business storage needs.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 px-8 py-4 rounded-xl font-semibold hover:bg-zinc-100 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
              >
                Book Now <ArrowRight size={20} />
              </Link>
              <Link
                to="/containers"
                className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-zinc-900 transition-all backdrop-blur-sm"
              >
                View Containers
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl">
              {[
                { label: "Container Sizes", value: "2" },
                { label: "Years Experience", value: "10+" },
                { label: "Secure Yard", value: "24/7" },
              ].map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + idx * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                >
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-zinc-300">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES / DETAILS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our Storage Solutions
            </h2>
            <p className="text-zinc-600 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              At Sahar Self Storage Containers, we understand that every storage need is unique. 
              Whether you're a homeowner clearing space during renovations, a tradesperson needing 
              secure tool storage, or a business requiring long-term inventory solutions, our 
              professionally managed container storage yard offers the flexibility and security you demand.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* 20ft */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="group space-y-6 bg-gradient-to-br from-zinc-50 to-white p-8 rounded-2xl shadow-lg border border-zinc-200 hover:shadow-xl transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <Warehouse size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold">20ft Containers</h3>
                  <p className="text-zinc-500 text-sm mt-1">Perfect for personal & trade use</p>
                </div>
              </div>
              <p className="text-zinc-600 text-lg leading-relaxed">
                Our 20ft containers provide ample space for household items, tools, equipment, 
                seasonal stock, or small business inventory. With ground-level access and robust 
                steel construction, your belongings remain protected from the elements while staying 
                easily accessible whenever you need them.
              </p>
              <ul className="space-y-3 pt-4">
                {[
                  "Weather-resistant steel construction",
                  "Convenient ground-level access",
                  "Perfect for personal or trade use",
                  "Secure locking mechanisms",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-zinc-700"
                  >
                    <CheckCircle
                      size={20}
                      className="text-blue-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/containers"
                className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all mt-4"
              >
                Learn More <ArrowRight size={18} />
              </Link>
            </motion.div>

            {/* 40ft */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="group space-y-6 bg-gradient-to-br from-zinc-50 to-white p-8 rounded-2xl shadow-lg border border-zinc-200 hover:shadow-xl transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <Truck size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold">40ft High Cube</h3>
                  <p className="text-zinc-500 text-sm mt-1">Ideal for commercial storage</p>
                </div>
              </div>
              <p className="text-zinc-600 text-lg leading-relaxed">
                When you need maximum capacity, our 40ft high cube containers deliver. The extra 
                height and doubled floor space make them ideal for commercial operations, bulk goods 
                storage, construction equipment, and businesses with extensive inventory requirements. 
                Cost-effective and spacious.
              </p>
              <ul className="space-y-3 pt-4">
                {[
                  "Increased internal height for tall items",
                  "Ideal for pallets, machinery & bulk storage",
                  "Cost-effective large-scale storage",
                  "Suitable for long-term commercial use",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-zinc-700"
                  >
                    <CheckCircle
                      size={20}
                      className="text-emerald-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/containers"
                className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:gap-3 transition-all mt-4"
              >
                Learn More <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        
        </div>
        
      </section>

      {/* TRUST / CONTACT */}
      <section className="py-24 bg-gradient-to-br from-zinc-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Choose Sahar Storage
            </h2>
            <p className="text-zinc-600 text-lg max-w-2xl mx-auto">
              Trusted by homeowners, tradespeople, and businesses across the region
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-24">
            {[
              {
                title: "Secure Yard",
                desc: "Our fully fenced, controlled-access storage yard ensures your containers and contents remain protected 24/7.",
                icon: <ShieldCheck size={32} />,
                gradient: "from-blue-500 to-blue-600",
                bg: "bg-blue-50",
              },
              {
                title: "Built to Last",
                desc: "Industrial-grade steel containers engineered to withstand harsh weather conditions and years of use.",
                icon: <Warehouse size={32} />,
                gradient: "from-emerald-500 to-emerald-600",
                bg: "bg-emerald-50",
              },
              {
                title: "Flexible Terms",
                desc: "Short or long-term storage options tailored for personal, trade, or business requirements.",
                icon: <Truck size={32} />,
                gradient: "from-amber-500 to-amber-600",
                bg: "bg-amber-50",
              },
            ].map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className={`${item.bg} p-8 rounded-2xl border border-zinc-200 space-y-4 transition-all hover:shadow-xl hover:-translate-y-1`}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                  <div className="text-white">{item.icon}</div>
                </div>
                <h4 className="text-2xl font-bold pt-2">{item.title}</h4>
                <p className="text-zinc-700 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-3xl p-12 text-white">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h3 className="text-3xl md:text-4xl font-bold mb-6">Get in Touch</h3>
              <p className="text-zinc-300 text-lg mb-12 max-w-2xl mx-auto">
                Ready to secure your storage space? Contact us today to discuss availability, 
                pricing, and find the perfect container solution for your needs.
              </p>
              <div className="mt-12 text-center">
  <p className="text-2xl font-bold text-white mb-6">
    📍 11 Hickman Avenue, E4 9JG
  </p>
  <div className="grid md:grid-cols-3 gap-6">
    <a href="tel:07446183394" className="flex flex-col items-center gap-3 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition">
      <Phone size={24} className="text-white" />
      <span className="font-semibold">07446 183 394</span>
    </a>
    <a href="tel:07915486419" className="flex flex-col items-center gap-3 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition">
      <Phone size={24} className="text-white" />
      <span className="font-semibold">07915 486 419</span>
    </a>
    <a href="mailto:sahar.storage2@gmail.com" className="flex flex-col items-center gap-3 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition">
      <Mail size={24} className="text-white" />
      <span className="font-semibold text-sm">sahar.storage2@gmail.com</span>
    </a>
  </div>
</div>
            </motion.div>
          </div>
        </div>
      </section>
      
    </div>
  );
}
